import { useState } from 'react';
import { supabase } from '../lib/supabase';

interface Location {
  lat: number;
  lng: number;
}

interface TaxiPlace {
  name: string;
  address: string;
  phone?: string;
  rating?: number;
  isOpen?: boolean;
  distance?: string;
  duration?: string;
  latitude: number;
  longitude: number;
}

const SEARCH_COOLDOWN = 60 * 1000; // 1 dakika (milisaniye cinsinden)
const STORAGE_KEY = 'lastSearchResults';

export function useNearbyTaxis() {
  const [places, setPlaces] = useState<TaxiPlace[]>(() => {
    const savedResults = localStorage.getItem(STORAGE_KEY);
    return savedResults ? JSON.parse(savedResults) : [];
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const searchNearbyTaxis = async (location: Location) => {
    const lastSearchTime = localStorage.getItem('lastTaxiSearchTime');
    const currentTime = Date.now();

    if (lastSearchTime) {
      const timeDiff = currentTime - parseInt(lastSearchTime);
      if (timeDiff < SEARCH_COOLDOWN) {
        const remainingTime = Math.ceil((SEARCH_COOLDOWN - timeDiff) / 1000);
        setError(`Yeni arama yapabilmek için ${remainingTime} saniye beklemelisiniz.`);
        return;
      }
    }

    setLoading(true);
    setError(null);
    
    try {
      const { data, error } = await supabase.rpc('nearby_taxi_stands', {
        lat: location.lat,
        long: location.lng,
        radius_km: 2,
        limit_count: 50
      });

      if (error) throw error;

      // Benzersiz taksi duraklarını bul
      const uniquePlaces = data.reduce((acc: any[], current: any) => {
        const isDuplicate = acc.some(place => 
          place.name.toLowerCase() === current.name.toLowerCase() && 
          place.address.toLowerCase() === current.address.toLowerCase()
        );

        if (!isDuplicate) {
          acc.push(current);
        } else {
          const existingIndex = acc.findIndex(place => 
            place.name.toLowerCase() === current.name.toLowerCase() && 
            place.address.toLowerCase() === current.address.toLowerCase()
          );

          if (existingIndex !== -1 && current.distance < acc[existingIndex].distance) {
            acc[existingIndex] = current;
          }
        }

        return acc;
      }, []);

      // En yakın 10 benzersiz durağı al
      const nearestUniquePlaces = uniquePlaces
        .sort((a, b) => a.distance - b.distance)
        .slice(0, 10);

      // Sonuçları formatla
      const formattedPlaces = nearestUniquePlaces.map(place => ({
        name: place.name,
        address: place.address,
        phone: place.phone,
        rating: place.rating,
        isOpen: true,
        distance: `${place.distance.toFixed(1)} km`,
        duration: `${Math.round(place.distance * 3)} dk`,
        latitude: place.latitude,
        longitude: place.longitude
      }));

      setPlaces(formattedPlaces);
      
      if (formattedPlaces.length === 0) {
        setError('Bu bölgede taksi durağı bulunamadı.');
      } else {
        localStorage.setItem('lastTaxiSearchTime', currentTime.toString());
        localStorage.setItem('hasSearchedTaxis', 'true');
        localStorage.setItem(STORAGE_KEY, JSON.stringify(formattedPlaces));
      }
    } catch (err) {
      console.error('Nearby search error:', err);
      setError(err instanceof Error ? err.message : 'Bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const hasSearchedBefore = () => {
    return localStorage.getItem('hasSearchedTaxis') === 'true';
  };

  const getLastSearchTime = () => {
    return localStorage.getItem('lastTaxiSearchTime');
  };

  const getRemainingCooldown = () => {
    const lastSearchTime = getLastSearchTime();
    if (!lastSearchTime) return 0;

    const timeDiff = Date.now() - parseInt(lastSearchTime);
    return Math.max(0, SEARCH_COOLDOWN - timeDiff);
  };

  return { 
    places, 
    loading, 
    error, 
    searchNearbyTaxis,
    hasSearchedBefore,
    getRemainingCooldown
  };
}